const DEV = false;

// @todo move to new file depending on Network
import type { AbiFunction } from "abitype";
import {
  type Abi,
  type Log,
  type TransactionReceipt,
  encodeFunctionData
} from "viem";

import {
  type AnyCell,
  type CellArray,
  type Cellified,
  type MapCell,
  type ValueCell,
  cellify,
  collector,
  copy,
  flattenCellArray,
  logger,
  reduce
} from "@okcontract/cells";
import {
  type ABI,
  type ABIExtra,
  type ABIMethod,
  ABIMethodAllowances,
  ABIMethodButton,
  ABIMethodImage,
  ABIMethodInfo,
  ABIMethodPre,
  ABIMethodTitle,
  type ABIValue,
  ChainQuery,
  type OKToken,
  OKWidgetApproveStep,
  OKWidgetCallStep,
  OKWidgetNetworkTXStep,
  OKWidgetSigStep,
  type OKWidgetStep,
  type OKWidgetStepType,
  TokenCurrentChain,
  type TokenMarketData,
  TokenMarketDataQuery
} from "@okcontract/coredata";
import {
  DataEditor,
  type EditorParameters,
  NEW,
  type TypeDefinitionFn,
  type TypeScheme,
  emptyValueOfTypeDefinitionAux,
  extractValidCells,
  isValid,
  objectDefinition
} from "@okcontract/fred";
import {
  type Environment,
  type MonoType,
  Rational,
  type ValueDefinition,
  newTypeScheme,
  typeNumber,
  typeString
} from "@okcontract/lambdascript";
import type { Value } from "@okcontract/lambdascript/src/eval";
import type {
  Chain,
  ChainAddress,
  ChainType,
  GetEstimateGas
} from "@okcontract/multichain";
import {
  type CallQueryType,
  type RawRPCQuery,
  getTransactionReceipt,
  nativeBalance
} from "@okcontract/multichain";
import { convertToNativeAddressesAndBigInt } from "@scv/cache";
import { toTitleCase } from "@scv/utils";

import {
  $valueDefinition,
  abiArgDefinition,
  extractArgIndex,
  findMethod,
  getAbiFunction,
  inputsPosition,
  isAnonymousArg,
  mergeExtraABIValue,
  typeFromABIParam
} from "./abi";
import type { AnalyzedLog } from "./analyze";
import { findObjectValue } from "./cellObject";
import type { OKCore } from "./coreExecution";
import { approve_method } from "./erc20";
import type { OKPage } from "./instance";
import { EnvKeyChain, EnvKeySelf, EnvKeyValue } from "./keys";
import { typeAddress } from "./lambdascript";
import {
  type AnyContractQuery,
  type DataOfContractQuery,
  type OKContract,
  OKContractManager
} from "./okcontract";
import { createProgram, getMissingInputs } from "./program";
import { type Step, type TXRequest, getOrgName } from "./steps";
import type { SentTransaction } from "./tx";
import { reactiveUncellify } from "./uncellify";

let txCounter = 0;

type Options = {
  abi?: Abi;
  // @todo should be an Environment directly
  extraEnv?: ValueDefinition[];
  settingsEnv?: Environment;
  forceTXGeneration?: boolean;
  /** override title */
  title?: MapCell<unknown[], false>;
  /** override info */
  info?: MapCell<unknown[], false>;
  /** override org */
  org?: MapCell<string, false>;
  okc?: OKContractManager;
  // Override the editor: e.g. for approval steps
  editor?: AnyCell<DataEditor>;
  isPreOk?: MapCell<boolean, false>;
};

export const filterMapByKeys = <T>(
  map: Map<string, T>,
  predicate: (key: string) => boolean
): Map<string, T> =>
  new Map([...map.entries()].filter(([key]) => predicate(key)));

/**
 * OKTransaction is a class containing all information relating to a
 * single blockchain transaction from its creation (inputs), computations,
 * submission and log analysis after mining.
 */
export class OKTransaction<Query extends AnyContractQuery> {
  private readonly _instance: OKPage;
  private _counter: number;
  private readonly _core: OKCore;
  private readonly _okc: OKContractManager;
  private readonly _options: Options;

  readonly okStep: Cellified<Step<OKWidgetStepType>>;
  /** stepper env */
  readonly $value: ValueCell<Rational>;
  readonly function: AnyCell<AbiFunction>;
  /** check if target method is on the right chain*/
  readonly chainOK: AnyCell<boolean>;
  readonly okContract: AnyCell<OKContract<Query>>;
  readonly contractChain: AnyCell<ChainType>;
  readonly chain: AnyCell<Chain>;
  readonly chains: AnyCell<Map<ChainType, ChainAddress<"evm">>>;
  readonly address: AnyCell<ChainAddress>;
  readonly contractData: AnyCell<DataOfContractQuery<Query>>;
  readonly abi: AnyCell<ABI>;
  readonly abix: AnyCell<ABIExtra>;
  //  basic env data
  readonly inputEnv: Environment;
  /** schema of the transaction */
  readonly schema: TypeScheme;
  /** tx data (from schema values) */
  readonly data: AnyCell<Environment>;
  readonly editor: AnyCell<DataEditor>;
  /** OKTransaction environment */
  readonly env: MapCell<Environment, false>;
  readonly missingInputs: MapCell<string[], false>;
  readonly org: MapCell<string, false>;
  readonly btnLabel: MapCell<string, false>;
  readonly title: MapCell<unknown[], false>;
  readonly info: MapCell<unknown[], false>;
  readonly infoLine: MapCell<string, false>;
  readonly button: MapCell<Value<string>[], false>;
  readonly image: MapCell<string, boolean>;
  readonly allowances: MapCell<[string, Value<unknown>][], boolean>;
  /** tx inputs */
  readonly txInputs: MapCell<AnyCell<unknown>[], false>;
  private _txInputsUncellified: AnyCell<unknown[]>;
  readonly inputsOK: MapCell<boolean, false>;
  /** if preconditions are met */
  readonly isPreOk: MapCell<boolean, false>;
  /** if tx can be sent */
  readonly canSendTX: MapCell<boolean, false>;
  /** this transaction can be skipped */
  readonly skip: AnyCell<boolean>;
  /** encoded data */
  readonly encodedData: MapCell<`0x${string}`, false>;
  /** the tx to be sent */
  readonly tx: MapCell<{ tx: TXRequest; gas_amount: bigint }, false>;
  /** the result of readonly method */
  readonly readOutput: MapCell<Record<number, AnyCell<unknown>>, false>;
  /** the sent transaction */
  readonly sentTx: ValueCell<SentTransaction[]>;
  /** tx receipt */
  readonly receipt: AnyCell<TransactionReceipt>;
  /** list of receipts */
  readonly receipts: ValueCell<TransactionReceipt[]>;
  /** raw logs from receipt */
  readonly rawLogs: AnyCell<AnyCell<Log>[]>;
  /** tx logs */
  readonly $logs: AnyCell<AnyCell<AnalyzedLog>[]>;
  /** prettified logs */
  readonly prettyLogs: CellArray<string[]>;
  /** tx is processing */
  readonly isSending: ValueCell<boolean>;
  /** tx has be processed */
  readonly isDone: ValueCell<boolean>;
  readonly decimals: AnyCell<Rational>;
  readonly tokenPriceData: AnyCell<TokenMarketData>;
  readonly currency: AnyCell<OKToken>;

  constructor(
    instance: OKPage,
    step: Cellified<Step<OKWidgetStepType>>,
    opts: Options = {
      settingsEnv: instance.core.Settings,
      forceTXGeneration: false
    }
  ) {
    this._counter = txCounter++;
    this._instance = instance;
    this._core = instance.core;
    const proxy = instance.proxy;
    this._okc = opts?.okc || new OKContractManager(instance);
    this._options = opts;

    this.okStep = step;
    DEV && logger(this.okStep);

    const query: MapCell<Query, true> = this.okStep.map(
      (step: Cellified<OKWidgetStep<OKWidgetStepType>>["value"]) =>
        (step?.q as AnyCell<Query>) || null,
      "step.query"
    );
    const sty: MapCell<OKWidgetStepType, true> = this.okStep.map(
      (step: Cellified<OKWidgetStep<OKWidgetStepType>>["value"]) =>
        step?.sty || null,
      "step.sty"
    );
    const fch: CellArray<ChainType> = this.okStep.map(
      (step: Cellified<OKWidgetStep<OKWidgetStepType>>["value"]) =>
        step?.fch || null,
      "step.fch"
    );
    const flatForceChains = flattenCellArray(proxy, fch, "ffch");
    flatForceChains.subscribe(async (l) => {
      if (!(l instanceof Error)) {
        const current = await instance.wantedChain.consolidatedValue;
        if (l?.length && !l.includes(current)) instance.wantedChain.set(l[0]);
      }
    });

    this.okContract = this._okc.get(query, { abi: opts?.abi });
    this.address = this.okContract.map((okc) => okc.address);
    this.contractData = this.okContract.map((okc) => okc.data);
    this.abi = this.okContract.map((okc) => okc.abi);
    const parsedAbi = this.abi.map((abi) => abi?.parsed || null);
    this.abix = this.okContract.map((okc) => okc.abix);
    const okEnv = this.okContract.map((okc) => okc.env);

    const coll =
      collector<MapCell<Map<ChainType, ChainAddress<"evm">>, false>>(proxy);
    this.chains = proxy.mapNoPrevious(
      [this.okContract, flatForceChains],
      (okc, fch) =>
        fch === null || !fch.length
          ? okc.chains
          : coll(
              proxy.map([okc.chains], (ch) =>
                filterMapByKeys(ch, (key) => fch.includes(key))
              )
            ),
      `okTX.${this._counter}:chains`
    );
    this.contractChain = proxy.map(
      [this.okContract, flatForceChains],
      (okc, fch) =>
        fch === null || fch.length === 0
          ? okc.chain
          : // @todo collect
            proxy.map([okc.chain], (wanted) =>
              fch.includes(wanted) ? okc.chain : fch[0]
            ),
      `okTX.${this._counter}:contractChain`
    );

    this.chain = proxy.map(
      [this.contractChain, this._core.Chains],
      (ch, chains) => chains[ChainQuery(ch)] || null,
      `okTX.${this._counter}:chain`
    );

    // tx method name (as cell)
    const meth = this.okStep.map(
      (step: Cellified<Step<OKWidgetStepType>>["value"]) => step?.m || null
    );
    // tx function
    this.function = proxy.map(
      [parsedAbi, meth],
      (_abi, _method) => getAbiFunction(_abi, _method),
      `okTX.${this._counter}:function`
    );
    DEV && logger(this.function);
    const methodName = this.function.map(
      (m) => m?.name || null,
      `okTX.${this._counter}:methodName`
    );

    this.chainOK = proxy.map(
      [
        sty,
        this.contractChain, //this.core.CurrentChain,
        this.abi,
        this.function,
        flatForceChains
      ],
      (_sty, _chain, _abi, _method, _fch) => {
        if (!_method || !_abi) return false; // @todo false
        if (_method?.stateMutability === "view") return true;
        // if (_sty === OKWidgetNetworkTXStep)
        //   return find(proxy, fch, (v) => v === _chain); // _fch.includes(_chain);
        // @previously isOnCurrentChain(_sty, _fch, _chain, _abi);
        return _sty === OKWidgetNetworkTXStep // || _sty === OKWidgetApproveStep
          ? _fch === null || _fch.includes(_chain)
          : _sty === OKWidgetCallStep || _sty === OKWidgetApproveStep
            ? _abi?.addr?.chain === _chain
            : _sty === OKWidgetSigStep;
      },
      `okTX.${this._counter}:chainOK`
    );
    DEV && logger(this.chainOK);

    const xm = proxy.mapNoPrevious(
      [this.okStep],
      (step) => step?.xm || null,
      `okTX.${this._counter}:xm`
    ) as unknown as Cellified<ABIMethod>;
    DEV && logger(xm);

    const uxm = reactiveUncellify(proxy, xm, `okTX.${this._counter}:uxm`);

    /**
     * merged all definitions (sorted in order of priority) for a given method from:
     * - abix values (common abix definitions)
     * - abix method (definitions for a given method)
     *  - xm (definitions from the widget)
     * @todo check that its all ABIValues
     * @todo this should be Cellified too, once Abix is Cellified
     */
    const abiMethod = proxy
      .map(
        [uxm, methodName, this.abix],
        async (xm, m, abix) =>
          ({
            ...abix?.values,
            ...abix?.methods?.[m],
            ...xm
          }) as ABIMethod
      )
      // merge
      .map((values) => {
        // add $address to ABIValues for token / contract and token-chain
        const res = Object.fromEntries(
          Object.entries(values).map(([k, v]) =>
            ["token", "contract", TokenCurrentChain].includes(v?.ty) && v?.v
              ? [k, { ...v, v: `$address(${v?.v})` }]
              : [k, v]
          )
        );
        return res;
      }, `okTX.${this._counter}:abiMethod`) as AnyCell<ABIMethod>;
    DEV && logger(abiMethod);

    const program = abiMethod.map(
      (abiMethod) => createProgram(abiMethod),
      `okTX.${this._counter}:program`
    );
    DEV && logger(program);

    const types = instance.dataTypeScheme();
    const typesCell = proxy.new(types, `okTX.${this._counter}:types`);
    // build envs
    this.$value = proxy.new(new Rational(0), `okTX.${this._counter}:$value`);

    const coreEnv = proxy.map(
      [okEnv],
      (env) =>
        env.withValueTypes(
          [EnvKeyValue, this.$value, newTypeScheme(typeNumber)],
          [EnvKeySelf, this._core.WalletID, newTypeScheme(typeAddress)],
          [EnvKeyChain, this.contractChain, newTypeScheme(typeString)],
          ...(opts?.extraEnv || []),
          ...(opts?.settingsEnv?.valueTypes() || [])
        ),
      `okTX.${this._counter}:coreEnv`
    );
    const getKeys = (env: Environment) => env.keys(false);
    const getValues = (env: Environment) => env.values;
    DEV && logger(coreEnv, getValues);

    // partialEnv only serves to hold enough values to evaluate
    // the empty values, it should be discarded when reduce the
    // whole program afterwards.
    // @todo check that no computation are made in double, maybe
    // reuse some env cells.
    const partialEnv = proxy.mapNoPrevious(
      [coreEnv, program],
      async (env, prog) => {
        const [out, _rem, _deps] = await prog.partialReduce(env);
        // console.log(`okTX(${this._counter}):partialEnv`, { rem, deps });
        return out;
      },
      `okTX.${this._counter}:partialEnv`
    );
    DEV && logger(partialEnv, getValues);

    this.missingInputs = proxy.map(
      [program, this.function, partialEnv, abiMethod], // @todo was preEnv
      (prog, fn, env, meth) =>
        getMissingInputs(prog, fn, env, Object.keys(meth)),
      `okTX.${this._counter}:missingInputs`
    );
    DEV && logger(this.missingInputs);

    // constant cell
    const number = proxy.new(typeNumber, "typeNumber");

    // @todo use mapArray to benefit from reuse + collection
    // @todo probably no need to react on wallet change to recompute definitions
    const missingDefs = proxy.mapNoPrevious(
      [this.missingInputs, partialEnv], // instance.core.WalletID
      (inputs, env) => {
        const res: [string, TypeDefinitionFn, AnyCell<MonoType>][] = [];
        for (const key of inputs) {
          // console.log("missingDefs", { key });
          const lowKey = key.toLowerCase();
          if (key === EnvKeyValue) {
            res.push([lowKey, $valueDefinition(instance, this.abi), number]);
            continue;
          }
          const anonKeyIndex = isAnonymousArg(key)
            ? extractArgIndex(key)
            : null;
          const abiParam = this.function.map(
            (_af) =>
              _af.inputs.find((_input, index) =>
                anonKeyIndex !== null
                  ? index === anonKeyIndex
                  : _input.name === key
              ) ||
              // null if not a direct parameter but comes from ABIMethod
              null,
            `missingDefs.abiParam:${key}`
          );
          DEV && logger(abiParam);
          const abiValue = abiMethod.map(
            // @ts-ignore strange type error
            (am) => findMethod(am, key),
            `missingDefs.abiValue:${key}`
          ) as MapCell<ABIValue, false>;
          DEV && logger(abiValue);
          const definition = abiArgDefinition(
            instance,
            partialEnv,
            this.function,
            abiValue,
            abiParam
          );
          // console.log("missingDefs", simplifier({ abiValue, definition }));
          const merged = mergeExtraABIValue(proxy, definition, abiMethod, key);
          DEV && logger(merged);
          const def = () => merged;
          res.push([
            lowKey,
            def,
            proxy.map([abiParam, abiValue], typeFromABIParam, `at:${key}`)
          ]);
        }
        return res;
      },
      `okTX.${this._counter}:missingDefs`
    );
    DEV && logger(missingDefs);

    const values = missingDefs.map(
      (arr) => Object.fromEntries((arr || []).map(([k, v, _]) => [k, v])),
      `okTX.${this._counter}:values`
    );
    this.schema = {
      types: typesCell,
      values: objectDefinition(proxy, values),
      gs: proxy.new([], `okTX.${this._counter}:schema.gs`)
    };

    // Environment containing input data.
    const dataEnv = instance.newEnvironment({ noInstance: true });

    // data is the interaction inputs
    // @todo reuse previous values
    // @todo rewrite
    this.data = proxy.mapNoPrevious(
      [missingDefs, partialEnv], // @todo react on missing inputs
      async (defs, env) => {
        // @todo We could recreate the dataEnv each time... investigate
        // const dataEnv = instance.newEnvironment({ noInstance: true });

        // We either create a new key / value and add it to this env
        // or if the key is already existing in preEnv we retrieve it.
        // Note: empty has already been filtered with the required inputs.
        // If the key is in dataEnv, we conserve it.

        // @todo remove cells in dataEnv that are not needed anymore.
        const rem = [];
        for (const key of Object.keys(dataEnv.values))
          if (!defs.find(([k, _t, _m]) => k === key)) rem.push(key);
        console.log("data", { rem });
        for (const key of rem) dataEnv.removeKey(key);

        for (const [key, typeDef, monoType] of defs)
          if (dataEnv.value(key) === undefined) {
            // We don't override previous definitions.
            // @todo we should not need to get the cell here
            const tyCell = typeDef();
            const ty = await tyCell.get();
            // if (env.value(key) !== undefined) {
            //   DEV &&
            //     console.warn(
            //       `okTX.${this._counter}: Key ${key} exists in partialEnv`
            //     );
            //   // continue;
            // }
            const cell =
              // reuse value from preEnv, e.g. `$value`
              env.value(key) ||
              // create new one
              cellify(
                proxy,
                await emptyValueOfTypeDefinitionAux(proxy, types, ty, {
                  oneElementInArray: true,
                  deep: false, // @todo check
                  label: `empty:${key}`
                }),
                `data:${key}`
              );
            // The MonoType should not change as it will not be reactive.
            const _mt = await monoType.consolidatedValue;
            // console.log("data", {
            //   key,
            //   _mt,
            //   prev: env.value(key),
            //   cell,
            //   monoType
            // });
            if (!(_mt instanceof Error))
              dataEnv.addValueType(key, cell, newTypeScheme(_mt));
          }

        return dataEnv;
      },
      `okTX.${this._counter}:data`
    );
    DEV && logger(this.data, getValues);

    this.env = proxy.mapNoPrevious(
      [program, partialEnv, this.data],
      (prog, env, data) => prog.reduce(env.mergeWith(undefined, data)),
      `okTX.${this._counter}:env`
    );
    DEV && logger(this.env, getKeys);

    const editorParameters = {
      mode: NEW,
      showLabels: false,
      name: "AFE"
    } as EditorParameters;

    const editorData = this.data.map(
      (data) => data?.values || null,
      `okTX.${this._counter}:eD`
    );
    // @todo should we change the editor that much?

    this.editor =
      opts?.editor ||
      proxy.map([this.data, this.env], (data, env, prev) => {
        // console.log("okTX: new editor", this._counter);
        return (
          prev ||
          // @todo auto-collect pointers in cells?
          new DataEditor(
            proxy,
            copy(proxy, editorData, `okTX.${this._counter}:eD.c`), // proxy.new(data.values, "editor.values"),
            this.schema,
            env,
            editorParameters
          )
        );
      });

    // const root = dataTree(proxy, editorData, this.schema);
    const root = this.editor.map((ed) => ed.root, `okTX.${this._counter}:root`);
    const validInputs = extractValidCells(proxy, root);
    this.inputsOK = isValid(proxy, validInputs);

    // @todo could be use to retrieve values from previous steps
    // @todo do we need/want that?
    // proxy.map(
    //   [this.sEnv, this.env],
    //   async (_sEnv, env) => {
    //     const empty = {
    //       ...(await emptyValueOfTypeDefinition(proxy, this.schema, ltd)?.get()),
    //     };
    //     const values = Object.keys(await this.schema.values.get());
    //     // keep already defined value in env
    //     // else set default empty value
    //     const data = values.reduce(
    //       (acc, value) => ({
    //         ...acc,
    //         [value]: _sEnv?.[value] || empty?.[value],
    //       }),
    //       {}
    //     );
    //     // this.data.set(data);
    //     return null;
    //   },
    //   "okTX.setData"
    // );

    // @move to core
    // @todo probably not needed anymore
    // const trueCell = proxy.new(true, "true");
    // @move to core
    // const falseCell = proxy.new("false", "falseExpr");

    const skip = proxy.map(
      [this.okStep],
      (step, _r) => step?.skip || "false",
      `okTX.${this._counter}:(skip)`
    ) as MapCell<string | null, true>;

    this.skip = proxy.map(
      [skip, this.env],
      (_skip, _env) =>
        _env.evaluateString(_skip) as Promise<MapCell<boolean, false>>,
      `okTX.${this._counter}:skip`
    );

    // @todo rewrite
    this.skip.subscribe((_skip) => {
      if (_skip instanceof Error) return;
      // console.log("_approval", { _skip });
      this.isDone.set(_skip);
    });

    this.isPreOk =
      opts?.isPreOk ||
      proxy.mapNoPrevious(
        [this.env, abiMethod],
        (_env, am) => {
          if (!am?.[ABIMethodPre]) return true;
          const expr = am[ABIMethodPre]?.map((def) => `(${def})`)?.join("&&");
          return _env.evaluateString(expr) as Promise<AnyCell<boolean>>;
        },
        `okTX.${this._counter}:isPreOk`
      );

    const makeTitle = () => {
      const title = proxy.map(
        [xm],
        (_xm, _r) => (_xm?.[ABIMethodTitle] as Cellified<string[]>) || [],
        `okTX.${this._counter}:xmTitle`
      ) as unknown as Cellified<string[]>;
      const titleDef = proxy.map(
        [title, sty, abiMethod, this.contractData],
        (_title, sty, abi_method, con) => {
          return (
            (_title?.length > 0
              ? flattenCellArray(proxy, title)
              : abi_method?.[ABIMethodTitle] || [
                  `"${
                    con?.name ||
                    con?.id ||
                    (sty === OKWidgetSigStep ? "Sign" : "")
                  }"`
                ]) || []
          );
        }
      );
      return proxy.map(
        [titleDef, this.env],
        (_titleDef, _env) => _env.evaluateStringArray(_titleDef),
        "okTX:title"
      );
    };
    // @todo title should be computed directly in the program
    this.title = this._options?.title || makeTitle();

    const makeInfo = () => {
      const info = proxy.map(
        [xm],
        (_xm) => _xm?.[ABIMethodInfo] || null,
        `okTX.${this._counter}:(info)`
      );
      // @previously default_info_txt(_info, _methodName, _abiMethod),
      const infoDef = proxy.map(
        [info, methodName, abiMethod],
        (_info, m, _abiMethod) => {
          // @todo maybe this should be done only once in methodName...
          const name = m?.includes("(") ? m.substring(0, m.indexOf("(")) : m;
          return _info?.length > 0
            ? flattenCellArray(proxy, info)
            : _abiMethod?.methods?.[name]?.[ABIMethodInfo] || [];
        },
        `okTX.${this._counter}:infoDef`
      );
      return proxy.map(
        [infoDef, this.env],
        (info: string[], _env) =>
          // console.log("info", { chain: _env.value(EnvKeyChain) });
          _env.evaluateStringArray(info),
        `okTX.${this._counter}:info`
      );
      // @todo restore fully cellified
      // this.info = this.env.map(
      //   (_env) =>
      //     mapArray(
      //       proxy,
      //       infoDef,
      //       (v) => _env.evaluateString(v),
      //       `okTX.${this._counter}:[info]`
      //     ),
      //   `okTX.${this._counter}:info`
      // );
    };

    // @todo info and infoLine should be computed directly in the program
    this.info = this._options?.info || makeInfo();
    this.infoLine = reduce(
      proxy,
      this.info as unknown as CellArray<unknown>,
      (acc, v) => `${acc} ${v?.toString()}`,
      ""
    );

    const allowanceDefs = proxy.map(
      [abiMethod],
      (_abiMethod) =>
        _abiMethod?.[ABIMethodAllowances]
          ? Object.entries(_abiMethod[ABIMethodAllowances])
          : [],
      `okTX.${this._counter}:allowanceDefs`
    );

    this.allowances = proxy.map(
      [allowanceDefs, this.env],
      async (_allowances, _env) => {
        const allowances = [];
        for (const [key, def] of _allowances)
          allowances.push([key, await _env.evaluateString(def)]);
        return allowances;
      },
      `okTX.${this._counter}:allowances`
    );

    const button = proxy.map(
      [xm],
      async (_xm, _r) => _xm?.[ABIMethodButton] || null,
      `okTX.${this._counter}:(button)`
    ) as unknown as Cellified<string[]>;
    // @previously default_btn_txt(_button, _methodName, _abiMethod),
    const buttonDef = proxy.map(
      [button, methodName, abiMethod],
      (_button, m, abi_method) => {
        const name = m?.includes("(") ? m.substring(0, m.indexOf("(")) : m;
        return (
          (_button?.length
            ? flattenCellArray(proxy, button)
            : abi_method?.[ABIMethodButton] ||
              (name !== undefined
                ? [
                    // proxy.new(
                    `"${name}"`
                    // , `default_btn:${name}`)
                  ]
                : [])) || []
        );
      },
      `okTX.${this._counter}:buttonDef`
    );
    // @todo removed method
    this.button = proxy.map(
      [buttonDef, this.env, this.okStep],
      async (_buttonDef, _env, _step) => _env.evaluateStringArray(_buttonDef),
      `okTX.${this._counter}:button`
    ) as MapCell<Value<string>[], false>;

    this.btnLabel = proxy.mapNoPrevious(
      [sty, methodName, this.button],
      // @ts-expect-error @todo
      (_sty, _methodName, _button) => {
        if (_button?.length) return _button[0];
        switch (_sty) {
          case OKWidgetCallStep:
            return toTitleCase(_methodName);
          case OKWidgetNetworkTXStep:
            return "Execute";
          case OKWidgetApproveStep:
            return approve_method;
          case OKWidgetSigStep:
            return "Sign";
        }
        return "";
      },
      `okTX.${this._counter}:btnLabel`
    );

    const imageDef = proxy.map(
      [xm],
      (_xm, _r) => _xm?.[ABIMethodImage] || null,
      `okTX.${this._counter}:imageDef`
    );
    this.image = proxy.map(
      [imageDef, this.env],
      (urlExpression: string, env) =>
        // @previously evalImageURL(_env, _imageDef),
        {
          if (!urlExpression) return null;
          // @todo factor all .startsWith("ipfs://") in utility function
          if (
            urlExpression.startsWith("https://") ||
            urlExpression.startsWith("ipfs://")
          )
            return urlExpression;
          // @todo (EvalOption.FailUndefined)
          return (
            (env.evaluateString(urlExpression) as Promise<AnyCell<string>>) ||
            "loading"
          );
        },
      `okTX.${this._counter}:image`
    );

    const makeOrg = () => {
      const org = proxy.mapNoPrevious(
        [this.okStep],
        (step) => step?.org || null,
        `okTX.${this._counter}:(org)`
      ) as MapCell<string, true>;
      // @todo also did depend on this.function
      return proxy.mapNoPrevious(
        [sty, query, org, this.env],
        (_sty, _q, _org, _env) => getOrgName(this._core, _env, _q, _sty, _org),
        `okTX.${this._counter}:org`
      );
    };

    this.org = this._options?.org || makeOrg();

    // Transaction Data
    // @todo except PayMaster
    const nativeValue = proxy.map(
      [this.$value, this.function],
      (_$value, _method) =>
        _method?.stateMutability !== "payable" || !_$value
          ? new Rational(0)
          : _$value,
      `okTX.${this._counter}:nativeValue`
    );

    // Tx generation
    this.txInputs = proxy.map(
      [this.function, this.env, this.chainOK, this.inputsOK],
      (_method, _env, _okChain, _inputsOK) =>
        _inputsOK && _okChain && _method ? inputsPosition(_method, _env) : [],
      `okTX.${this._counter}:txInputs`
    );

    this._txInputsUncellified = reactiveUncellify(
      proxy,
      this.txInputs,
      "txInputsU"
    );

    const balance = nativeBalance(
      proxy,
      instance.rpc,
      this.contractChain,
      instance.core.WalletID
    );

    // @todo also check for gas (split canBuildTX, canSendTX)
    this.canSendTX = proxy.map(
      [this.function, this.isPreOk, this.inputsOK, nativeValue, balance],
      (met, pre, inputs, v, b) =>
        met &&
        met.stateMutability !== "view" &&
        inputs &&
        pre &&
        v.compare("<", new Rational(b)),
      `okTX.${this._counter}:canSendTX`
    );

    // @todo handle ApproveTX / NTX
    this.encodedData = proxy.map(
      [
        parsedAbi,
        methodName,
        this.chainOK,
        this.canSendTX,
        this._txInputsUncellified
      ],
      async (abi, functionName, _okChain, _canSendTX, _inputValues) => {
        if (!_canSendTX || !_okChain) return null;
        const args = convertToNativeAddressesAndBigInt(_inputValues);
        // @ts-ignore error infinite recursion in viem
        const encodedData = encodeFunctionData({
          abi,
          functionName,
          // @ts-expect-error readonly error
          args
        }) as `0x${string}`;
        return encodedData;
      },
      `okTX.${this._counter}:encodedData`
    );

    this.isSending = proxy.new(false, `okTX.${this._counter}:isSending`);
    // @ts-expect-error @todo Property 'gas_amount' is missing
    this.tx = proxy.mapNoPrevious(
      [this.canSendTX, this.skip],
      (_canSendTX, _skip) =>
        _canSendTX && (!_skip || opts.forceTXGeneration)
          ? instance._generateTX(
              this.address,
              this.encodedData,
              nativeValue,
              !opts.forceTXGeneration
            )
          : null,
      `okTX.${this._counter}:tx`
    );

    // @todo should be mapped?
    // @todo rewrite
    this.isDone = proxy.new(
      (async () => {
        const okStep = await step.get();
        if (okStep instanceof Error) throw okStep;
        // @todo check value
        return okStep?.sty?.value === OKWidgetApproveStep &&
          "done" in okStep &&
          // @ts-expect-error fix Cellified types
          okStep?.amount?.value !== BigInt(0)
          ? (okStep.done as boolean)
          : false;
      })(),
      `okTX.${this._counter}:isDone`
    );

    // method evaluation
    const canCallMethod = proxy.map(
      [this.chainOK, this.function, this.txInputs],
      (ok, fn, inputs) =>
        ok && fn?.outputs && fn?.stateMutability === "view" && inputs,
      `okTX.${this._counter}:canCallMethod`
    );
    const methodAddr = proxy.map(
      [this.address, canCallMethod],
      (_addr, _canCall) => (_canCall ? _addr : null),
      `okTX.${this._counter}:methodAddr`
    );
    this.readOutput = this._instance.rpc
      .call(methodAddr, parsedAbi, methodName, this.txInputs)
      .map((output) => {
        if (output === null) return null;
        if (output instanceof Error) throw output;
        if (Array.isArray(output))
          return Object.fromEntries(output.map((out, i) => [i, out]));
        return { 0: proxy.new(output) };
      });

    this.sentTx = proxy.new([], `okTX.${this._counter}:sentTx`);
    const sentTXHash = this.sentTx.map(
      (_sentTx) => _sentTx?.[_sentTx?.length - 1]?.hash || null,
      `okTX.${this._counter}:sentTXHash`
    );
    // last receipt
    this.receipt = getTransactionReceipt(
      this._instance.rpc,
      sentTXHash,
      this.contractChain
    );

    /**
     * receipts is a list of receipt since a given
     * OKTransaction could be repeated multiple times.
     */
    this.receipts = proxy.new([], `okTX.${this._counter}:receipts`);
    // add new receipts in receipts list and core
    this.receipt.subscribe(async (_receipt) => {
      if (_receipt instanceof Error) throw _receipt;
      if (!_receipt) return;
      // @todo why? we can have 2 tx in same block
      // @todo required?
      const exists = (l: TransactionReceipt[]) =>
        l.find((_r) => _r.blockHash === _receipt.blockHash);
      this.receipts.update((l) => (exists(l) ? [...l, _receipt] : undefined));
      this._core.Receipts.update((l) =>
        exists(l) ? [...l, _receipt] : undefined
      );
    });
    // listen to receipt to notify when a tx is completed
    this.receipt.subscribe((_receipt) => {
      if (_receipt instanceof Error) {
        this.isSending.set(false);
        return;
      }
      // @todo check if necessary for approve_method
      // if (!_receipt || this.function.value.name === approve_method) return;
      if (!_receipt) return;
      this.isSending.set(false);
      // @todo check if necessary for approve_method
      if (this.function.value.name === approve_method) return;
      this.isDone.set(true);
    });

    this.rawLogs = this.receipt.map(
      (_receipt) =>
        _receipt?.logs?.map((log) => proxy.new(log, "okTX.rawLog")) || [],
      "okTX.rawLogs"
    );

    this.$logs = instance.analyzeLogs(this.contractChain, this.rawLogs);
    this.prettyLogs = this.$logs.map(
      (logs) =>
        (logs || []).map((log) =>
          instance.logTitle(
            instance.core.WalletID, // @todo check wallet (should be the tx submitter)
            log
          )
        ),
      `okTX.${this._counter}:prettyLogs`
    );

    /** chain currency */
    // @todo move to OKCore
    this.currency = this.contractChain.map((_chain) => {
      const chain = findObjectValue(
        proxy,
        this._core.Chains,
        (v) => v.id === _chain
      ) as AnyCell<Chain>;
      const tokenQuery = chain.map((_chain) => _chain?.currency || null);
      return this._instance.local.unwrappedCell(tokenQuery);
    }, `okTX.${this._counter}:currency`) as AnyCell<OKToken>;

    /** token price data from server */
    const tmdQuery = this.currency.map((c) => {
      if (!c) return null;
      const coingecko = c.foreign?.find((f) => f.includes("coingecko"));
      if (!coingecko) return null;
      const id = coingecko.split(":")[1];
      return TokenMarketDataQuery(id);
    }, "tmq");
    this.tokenPriceData = this._instance.local.unwrappedCell(tmdQuery, "tpd");
  }

  /**
   * setValue sets the $value cell containing the transaction value.
   * @param v any float or integer number, without decimals conversion, or exact `bigint`
   * @example tx.setValue(0.001)
   */
  public setValue = (v: number | bigint) => {
    const chain = this.chain.value;
    if (chain instanceof Error) throw chain;
    const dec = chain.decimals === undefined ? 18 : chain.decimals;
    this.$value.set(new Rational(typeof v === "number" ? v * 10 ** dec : v));
  };

  /**
   * sendTX submits the transaction on-chain.
   * @returns block hash
   */
  public sendTX = async () => {
    const txData = await this.tx.consolidatedValue;
    if (txData instanceof Error) return null;
    const tx = txData.tx;
    // console.log({ tx });
    const walletID = await this._core.WalletID.consolidatedValue;
    if (walletID instanceof Error) return null;
    // @todo this is not always the current chain
    // const ch = await this._core.Chain.get();
    // if (ch instanceof Error) return;
    // @todo could be a Cell
    const chain = await this.chain.consolidatedValue;
    if (chain instanceof Error) return null;
    const input = {
      chain: chain.id,
      value: tx?.value,
      to: tx?.to,
      data: tx?.data,
      gasLimit: tx?.gasLimit,
      from: tx?.from
    };
    const conn = await this._core.Connector.get();
    // @todo maybe we could use the chain directly since we already have it
    const hash = await conn.sendTransaction(input);
    // console.log("SendTX= hash", { hash });
    if (!hash) return null;
    this.isSending.set(true);
    const method = await this.function.get();
    if (method instanceof Error) throw method;
    const tokenPrice = await this.tokenPriceData.get();
    const newTx = {
      ch: chain.id,
      hash,
      url: `${chain.explorer}/tx/${hash}`,
      fnName: method.name,
      gasCurPrice: tokenPrice instanceof Error ? 0 : tokenPrice?.current_price,
      sentAt: new Rational(Date.now()) // @todo should not be?
    } as SentTransaction;
    // add to okTransaction
    this.sentTx.update((sent) => [...sent, newTx]);
    // add to core
    this._core.SentTransactions.update((all) => [...all, newTx]);
    return hash;
  };

  /**
   * Sign the raw transaction, for the few EOAs that support it.
   * @todo should be a ValueCell?
   */
  public signTX = async () => {
    const txData = await this.tx.get();
    if (txData instanceof Error) return null;
    const tx = txData.tx;
    const walletID = await this._core.WalletID.get();
    if (walletID instanceof Error) return null;
    const ch = await this.chain.consolidatedValue;
    if (ch instanceof Error) return;
    const conn = await this._core.Connector.get();
    try {
      const input = {
        chain: ch.id,
        value: tx?.value,
        to: tx?.to,
        data: tx?.data,
        gasLimit: tx?.gasLimit,
        from: tx?.from
      };
      const sig = await conn.signTransaction(input);
      console.log("signTX", { sig });
    } catch (err) {
      console.log("signTX", err);
    }
  };

  public switchChain = () => {
    const chain = this.chain.value;
    if (chain instanceof Error) {
      console.error("switchChain", chain);
      return;
    }
    // console.log("Switching to", chain.id);
    this._core.SwitchChain(chain.id);
  };

  // once approve tx is done:
  // - we invalidate the current allowance query and retry with
  //   the receipt blockNumber
  // - once the query invalidation is confirm with the new value updated
  //   we manually set the OKTransaction to a done state and stop the
  //   isSending cell
  _approvalRefresh = async (
    _receipt: TransactionReceipt | Error,
    _allowanceQuery: AnyCell<[ChainType, RawRPCQuery]>,
    _estimateQuery: AnyCell<[ChainType, GetEstimateGas]>
  ) => {
    if (_receipt instanceof Error) throw _receipt;
    if (!_receipt) return;

    const q = await _allowanceQuery.consolidatedValue;
    if (q instanceof Error) throw q;
    const estimateQuery = await _estimateQuery.consolidatedValue;
    if (estimateQuery instanceof Error) throw estimateQuery;

    // @todo move to rpc
    const queryWithBlockNumber = {
      ...q[1],
      params: [q[1].params[0], `0x${_receipt.blockNumber.toString(16)}`]
    } as CallQueryType;

    // invalidating rpc query
    await this._instance.rpc.invalidate(q, {
      cb: () => {
        this.isSending.set(false);
        this.isDone.set(true);
      },
      replaceQuery: queryWithBlockNumber
    });
    await this._instance.rpc.invalidate(estimateQuery, {
      cb: () => {
        this.isSending.set(false);
        this.isDone.set(true);
      },
      replaceQuery: estimateQuery[1]
    });
  };

  public wait() {
    return this._instance.proxy.working.wait();
  }
}
